import { useState, useEffect, lazy, Suspense } from "react";
import { UNIT_PRICE, PACK_5_PRICE, PACK_10_PRICE } from "@/lib/bundlePricing";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { ValuesBar } from "@/components/ValuesBar";
import { ProductGrid } from "@/components/ProductGrid";
import { Footer } from "@/components/Footer";
import { PromoBanner } from "@/components/PromoBanner";
import { PackSelectorModal } from "@/components/PackSelectorModal";
import { Package, Flame } from "lucide-react";
import { useProductThumbnails } from "@/hooks/useProductThumbnails";
import { useLang } from "@/contexts/LanguageContext";
import { usePageMeta } from "@/hooks/usePageMeta";

// Below-the-fold — lazy loaded to improve LCP/FCP
const StoryPreview = lazy(() => import("@/components/StoryPreview").then(m => ({ default: m.StoryPreview })));
const FAQ = lazy(() => import("@/components/FAQ").then(m => ({ default: m.FAQ })));
const NewsletterSignup = lazy(() => import("@/components/NewsletterSignup").then(m => ({ default: m.NewsletterSignup })));
const InstagramSection = lazy(() => import("@/components/InstagramSection").then(m => ({ default: m.InstagramSection })));
const TikTokSection = lazy(() => import("@/components/TikTokSection").then(m => ({ default: m.TikTokSection })));
const MissionSection = lazy(() => import("@/components/MissionSection").then(m => ({ default: m.MissionSection })));
const DonationFeed = lazy(() => import("@/components/DonationFeed").then(m => ({ default: m.DonationFeed })));
const GateIntro = lazy(() => import("@/components/GateIntro"));

const SectionFallback = () => <div className="min-h-[200px]" />;

const GATE_SESSION_KEY = "elfaza_gate_seen";

function shouldSkipGate(): boolean {
  const params = new URLSearchParams(window.location.search);
  if (params.get("utm_source") || params.get("utm_medium") === "email" || params.get("ref") === "email") {
    return true;
  }
  if (window.location.hash === "#products") {
    return true;
  }
  return false;
}

const Index = () => {
  const { lang, t } = useLang();
  usePageMeta(
    lang === "en"
      ? {
          title: "EL FAZA — Premium Tunisian 3D Stickers | Made in Canada",
          description: "Premium 3D raised stickers celebrating Tunisian heritage. Handcrafted in Canada. Free shipping over $69 across Canada and the USA.",
          canonical: "/en",
        }
      : {
          title: "EL FAZA — Adhésifs 3D Tunisiens | Fierté & Racines",
          description: "Des adhésifs en relief qui portent en eux la fierté de nos racines et la gratitude envers le Canada 🍁 — le pays qui nous a tout donné pour réussir.",
          canonical: "/",
        },
  );
  const [showGate, setShowGate] = useState(() => {
    if (shouldSkipGate()) {
      sessionStorage.setItem(GATE_SESSION_KEY, "1");
      return false;
    }
    return !sessionStorage.getItem(GATE_SESSION_KEY);
  });
  const [packModal, setPackModal] = useState<{ open: boolean; size: number }>({ open: false, size: 5 });

  useEffect(() => {
    if (shouldSkipGate()) {
      const el = document.getElementById("products");
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 300);
      }
    }
  }, []);

  const handleGateComplete = () => {
    sessionStorage.setItem(GATE_SESSION_KEY, "1");
    setShowGate(false);
  };

  const pack5Savings = ((1 - PACK_5_PRICE / (5 * UNIT_PRICE)) * 100).toFixed(0);
  const pack10Savings = ((1 - PACK_10_PRICE / (10 * UNIT_PRICE)) * 100).toFixed(0);

  const pack5Thumbs = useProductThumbnails(5);
  const pack10Thumbs = useProductThumbnails(10);

  return (
    <div className="min-h-screen flex flex-col relative">
      {showGate && (
        <Suspense fallback={null}>
          <GateIntro onComplete={handleGateComplete} />
        </Suspense>
      )}

      <PromoBanner />
      <Navbar />
      <Hero />
      <ValuesBar />

      {/* Products */}
      <main className="bg-zellige">
        <div className="container mx-auto px-4 py-20 flex-1">
          <div id="products" className="text-center mb-14 scroll-mt-20">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-body font-medium mb-5">
              ✦ {t({ fr: "Collection", en: "Collection" })}
            </div>
            <h2 className="text-3xl md:text-5xl font-display font-bold text-foreground mb-3">
              {t({ fr: "Nos Adhésifs", en: "Our Stickers" })}
            </h2>
            <p className="text-muted-foreground font-body text-lg mt-2 max-w-lg mx-auto">
              {t({ fr: "Des adhésifs 3D en relief qui portent la fierté tunisienne 🇹🇳", en: "Premium 3D raised stickers carrying Tunisian pride 🇹🇳" })}
            </p>

            {/* Pack offers — VIP edition */}
            <div className="mt-14 max-w-4xl mx-auto px-2">
              <div className="flex items-center justify-center gap-3 mb-7">
                <div className="h-px flex-1 max-w-[80px] bg-gradient-to-r from-transparent via-primary/40 to-primary/60" />
                <span className="text-[11px] font-body font-bold uppercase tracking-[0.3em] text-primary flex items-center gap-2">
                  <span className="text-amber-500">✦</span> {t({ fr: "Offres VIP", en: "VIP Offers" })} <span className="text-amber-500">✦</span>
                </span>
                <div className="h-px flex-1 max-w-[80px] bg-gradient-to-l from-transparent via-primary/40 to-primary/60" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6 items-stretch">
                {/* Pack 5 — Argent */}
                <button
                  onClick={() => setPackModal({ open: true, size: 5 })}
                  className="group relative overflow-hidden rounded-[2rem] shadow-editorial hover:shadow-card-hover hover:-translate-y-2 active:scale-[0.98] transition-all duration-500 cursor-pointer text-left sm:mt-2"
                >
                  {/* Premium silver gradient border */}
                  <div className="absolute inset-0 rounded-[2rem] p-[2px] bg-gradient-to-br from-slate-300 via-slate-400 to-slate-200">
                    <div className="w-full h-full rounded-[1.9rem] bg-card" />
                  </div>

                  {/* Inner luxe gradient */}
                  <div className="absolute inset-[2px] rounded-[1.9rem] bg-gradient-to-br from-slate-200/[0.15] via-card to-slate-300/[0.12]" />
                  <div className="absolute inset-[2px] rounded-[1.9rem] bg-gradient-to-t from-slate-300/[0.08] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  {/* Silver corner accents with permanent metallic reflection */}
                  <div className="absolute top-2 left-2 w-20 h-20 border-t-2 border-l-2 border-slate-400/60 rounded-tl-[1.7rem] pointer-events-none" />
                  <div className="absolute bottom-2 right-2 w-20 h-20 border-b-2 border-r-2 border-slate-400/60 rounded-br-[1.7rem] pointer-events-none" />
                  {/* Permanent silver corner glints */}
                  <div className="absolute top-0 left-0 w-32 h-32 rounded-tl-[2rem] pointer-events-none bg-[radial-gradient(circle_at_top_left,hsl(0_0%_100%/0.55),hsl(220_15%_85%/0.25)_30%,transparent_65%)] mix-blend-screen" />
                  <div className="absolute bottom-0 right-0 w-32 h-32 rounded-br-[2rem] pointer-events-none bg-[radial-gradient(circle_at_bottom_right,hsl(0_0%_100%/0.5),hsl(220_15%_80%/0.22)_30%,transparent_65%)] mix-blend-screen" />
                  <div className="absolute top-0 right-0 w-24 h-24 rounded-tr-[2rem] pointer-events-none bg-[radial-gradient(circle_at_top_right,hsl(220_20%_92%/0.35),transparent_60%)]" />
                  <div className="absolute bottom-0 left-0 w-24 h-24 rounded-bl-[2rem] pointer-events-none bg-[radial-gradient(circle_at_bottom_left,hsl(220_20%_92%/0.35),transparent_60%)]" />

                  <div className="absolute inset-0 overflow-hidden rounded-[2rem] pointer-events-none">
                    <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-slate-200/25 to-transparent" />
                  </div>

                  {/* Silver ribbon */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10">
                    <div className="relative overflow-hidden px-5 py-1.5 rounded-b-2xl bg-gradient-to-r from-slate-500 via-slate-200 to-slate-500 text-white text-[11px] font-bold font-body flex items-center gap-1.5 shadow-[0_4px_14px_-2px_hsl(220_10%_40%/0.5)] tracking-[0.15em] uppercase">
                      <span className="relative z-10">✦</span>
                      <span className="relative z-10">{t({ fr: "Pack Argent", en: "Silver Pack" })}</span>
                      {/* Metallic shine sweep */}
                      <span className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/80 to-transparent blur-[2px] animate-silver-shine" />
                    </div>
                  </div>

                  {/* Header strip */}
                  <div className="relative h-24 pt-4 bg-white flex items-center justify-center gap-2 px-4 overflow-hidden mt-[2px] mx-[2px] rounded-t-[1.9rem]">
                    {(pack5Thumbs.length ? pack5Thumbs : Array(5).fill(null)).map((url, i) => (
                      <div
                        key={i}
                        className="w-12 h-12 flex items-center justify-center transition-all duration-500 group-hover:-translate-y-1 group-hover:rotate-[-3deg]"
                        style={{ transitionDelay: `${i * 50}ms` }}
                      >
                        {url ? (
                          <img
                            src={url}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-contain mix-blend-multiply drop-shadow-sm"
                            style={{ filter: "contrast(1.08) brightness(1.06)" }}
                          />
                        ) : (
                          <span className="text-2xl opacity-40">🇹🇳</span>
                        )}
                      </div>
                    ))}
                    <div className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-[hsl(var(--card))] to-transparent" />
                    <div className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-[hsl(var(--card))] to-transparent" />
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-[hsl(var(--card))] to-transparent" />
                  </div>

                  <div className="relative p-7 pt-5 flex flex-col items-center text-center gap-3">
                    <div className="flex items-center gap-2">
                      <Package className="w-5 h-5 text-slate-500" />
                      <h3 className="font-display font-bold text-2xl tracking-tight bg-gradient-to-r from-slate-600 via-slate-500 to-slate-600 bg-clip-text text-transparent">
                        {t({ fr: "Pack Argent", en: "Silver Pack" })}
                      </h3>
                    </div>
                    <p className="text-xs font-body text-muted-foreground -mt-1 tracking-wide">
                      {t({ fr: "5 adhésifs · Composition libre", en: "5 stickers · Build your own" })}
                    </p>

                    <div className="flex items-baseline gap-2 mt-2">
                      <span className="font-display font-extrabold text-4xl bg-gradient-to-br from-slate-600 to-slate-500 bg-clip-text text-transparent leading-none">
                        {PACK_5_PRICE.toFixed(2)}$
                      </span>
                      <span className="text-sm font-body text-muted-foreground/70 line-through">
                        {(5 * UNIT_PRICE).toFixed(2)}$
                      </span>
                    </div>

                    <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gradient-to-r from-slate-300/20 to-slate-400/20 border border-slate-400/40 text-slate-600 text-xs font-bold font-body tracking-wide">
                      <span className="text-slate-500">✦</span> {t({ fr: "Économise", en: "Save" })} {pack5Savings}%
                    </span>

                    <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-body font-bold bg-gradient-to-r from-slate-600 to-slate-500 bg-clip-text text-transparent group-hover:gap-2.5 transition-all">
                      {t({ fr: "Composer mon pack", en: "Build my pack" })} <span className="text-slate-500 transition-transform group-hover:translate-x-1">→</span>
                    </span>
                  </div>
                </button>

                {/* Pack 10 — VIP featured */}
                <button
                  onClick={() => setPackModal({ open: true, size: 10 })}
                  className="group relative overflow-hidden rounded-[2rem] shadow-editorial hover:shadow-card-hover hover:-translate-y-2 active:scale-[0.98] transition-all duration-500 cursor-pointer text-left sm:scale-[1.03] sm:mt-2"
                >
                  {/* Premium gold gradient border */}
                  <div className="absolute inset-0 rounded-[2rem] p-[2px] bg-gradient-to-br from-amber-400 via-primary to-amber-500">
                    <div className="w-full h-full rounded-[1.9rem] bg-card" />
                  </div>

                  {/* Inner luxe gradient */}
                  <div className="absolute inset-[2px] rounded-[1.9rem] bg-gradient-to-br from-primary/[0.08] via-card to-amber-500/[0.1]" />
                  <div className="absolute inset-[2px] rounded-[1.9rem] bg-gradient-to-t from-primary/[0.06] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  {/* Gold corner accents — more prominent */}
                  <div className="absolute top-2 left-2 w-20 h-20 border-t-2 border-l-2 border-amber-500/60 rounded-tl-[1.7rem] pointer-events-none" />
                  <div className="absolute bottom-2 right-2 w-20 h-20 border-b-2 border-r-2 border-amber-500/60 rounded-br-[1.7rem] pointer-events-none" />

                  <div className="absolute inset-0 overflow-hidden rounded-[2rem] pointer-events-none">
                    <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-amber-200/20 to-transparent" />
                  </div>

                  {/* VIP Crown ribbon */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10 animate-gold-pulse">
                    <div className="px-5 py-1.5 rounded-b-2xl bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 text-white text-[11px] font-bold font-body flex items-center gap-1.5 shadow-lg tracking-[0.15em] uppercase">
                      <span>👑</span> {t({ fr: "Pack VIP", en: "VIP Pack" })}
                    </div>
                  </div>

                  {/* Header strip with stickers preview */}
                  <div className="relative h-24 pt-4 bg-white flex items-center justify-center gap-1.5 px-4 overflow-hidden mt-[2px] mx-[2px] rounded-t-[1.9rem]">
                    {(pack10Thumbs.length ? pack10Thumbs : Array(10).fill(null)).map((url, i) => (
                      <div
                        key={i}
                        className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center transition-all duration-500 group-hover:-translate-y-1 group-hover:rotate-[-3deg]"
                        style={{ transitionDelay: `${i * 35}ms` }}
                      >
                        {url ? (
                          <img
                            src={url}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-contain mix-blend-multiply drop-shadow-sm"
                            style={{ filter: "contrast(1.08) brightness(1.06)" }}
                          />
                        ) : (
                          <span className="text-base opacity-40">🇹🇳</span>
                        )}
                      </div>
                    ))}
                    <div className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-[hsl(var(--card))] to-transparent" />
                    <div className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-[hsl(var(--card))] to-transparent" />
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-[hsl(var(--card))] to-transparent" />
                  </div>

                  <div className="relative p-7 pt-5 flex flex-col items-center text-center gap-3">
                    <div className="flex items-center gap-2">
                      <h3 className="font-display font-bold text-foreground text-2xl sm:text-[1.7rem] tracking-tight bg-gradient-to-r from-primary via-amber-600 to-primary bg-clip-text text-transparent">
                        {t({ fr: "Pack Signature", en: "Signature Pack" })}
                      </h3>
                    </div>
                    <p className="text-xs font-body text-muted-foreground -mt-1 tracking-wide">
                      {t({ fr: "10 adhésifs · L'expérience complète", en: "10 stickers · The full experience" })}
                    </p>

                    <div className="flex items-baseline gap-2 mt-2">
                      <span className="font-display font-extrabold text-5xl bg-gradient-to-br from-primary to-amber-600 bg-clip-text text-transparent leading-none">
                        {PACK_10_PRICE.toFixed(2)}$
                      </span>
                      <span className="text-sm font-body text-muted-foreground/70 line-through">
                        {(10 * UNIT_PRICE).toFixed(2)}$
                      </span>
                    </div>

                    <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gradient-to-r from-amber-500/15 to-primary/15 border border-amber-500/30 text-primary text-xs font-bold font-body tracking-wide">
                      <Flame className="w-3.5 h-3.5 text-amber-600" /> {t({ fr: "Économise", en: "Save" })} {pack10Savings}%
                    </span>

                    {/* VIP perks */}
                    <ul className="mt-2 space-y-1 text-[11px] font-body text-muted-foreground">
                      <li className="flex items-center gap-1.5"><span className="text-amber-500">✓</span> {t({ fr: "Livraison offerte incluse", en: "Free shipping included" })}</li>
                      <li className="flex items-center gap-1.5"><span className="text-amber-500">✓</span> {t({ fr: "Le meilleur prix unitaire", en: "Best per-unit price" })}</li>
                    </ul>

                    <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-body font-bold bg-gradient-to-r from-primary to-amber-600 bg-clip-text text-transparent group-hover:gap-2.5 transition-all">
                      {t({ fr: "Composer mon pack", en: "Build my pack" })} <span className="text-primary transition-transform group-hover:translate-x-1">→</span>
                    </span>
                  </div>
                </button>
              </div>

              <p className="text-center text-xs font-body text-muted-foreground mt-6 tracking-wide">
                ✦ {t({ fr: "Livraison gratuite dès 69$ · Composition libre parmi tous les modèles", en: "Free shipping over $69 · Mix and match across all designs" })} ✦
              </p>
            </div>
          </div>
          <ProductGrid />
        </div>
      </main>

      <div className="section-divider" />

      <Suspense fallback={<SectionFallback />}>
        <MissionSection />
      </Suspense>

      <div className="section-divider" />

      <div id="donations" className="scroll-mt-20">
        <Suspense fallback={<SectionFallback />}>
          <DonationFeed />
        </Suspense>
      </div>

      <div className="section-divider" />

      <Suspense fallback={<SectionFallback />}>
        <InstagramSection />
      </Suspense>

      <div className="section-divider" />

      <Suspense fallback={<SectionFallback />}>
        <TikTokSection />
      </Suspense>

      <div className="section-divider" />

      <Suspense fallback={<SectionFallback />}>
        <StoryPreview />
      </Suspense>

      <div className="section-divider" />

      <div className="container mx-auto px-4 py-14" data-newsletter>
        <Suspense fallback={<SectionFallback />}>
          <NewsletterSignup />
        </Suspense>
      </div>

      <div className="section-divider" />

      <Suspense fallback={<SectionFallback />}>
        <FAQ />
      </Suspense>

      <Footer />

      <PackSelectorModal
        open={packModal.open}
        onOpenChange={(open) => setPackModal((prev) => ({ ...prev, open }))}
        packSize={packModal.size as 5 | 10}
      />
    </div>
  );
};

export default Index;
